import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import * as XLSX from "xlsx";
import {
  readWorkbookRows,
  autoDetectColumns,
  detectHsCodeIdx,
  detectOriginIdx,
  buildExportAoa,
  rowsToDedupItems,
  resultsBySrcExpanded,
  groupByHskV2,
} from "./bulkClassifyXlsx";
import {
  submitJob,
  getJob,
  cancelJob,
  confirmSeed,
  retryFailed,
  startJobRun,
  getJobSource,
  getRatesSummary,
  getReqFlags,
  getHskDetail,
  listExportLayouts,
  saveExportLayout,
  deleteExportLayout,
} from "./bulkClassifyApi";
import { mergeLayout, projectAoa, aoaToGridData } from "./exportLayout";
import { createJobPoller } from "./jobPoller";
import {
  crossLabel,
  confidenceLabel,
  needsReview,
  masterLabel,
} from "./crossLabels";
import { formatProgress } from "./progressFormat";
import BulkJobList from "./BulkJobList";
import StepTabs from "./StepTabs";
import {
  STEP,
  defaultStepFor,
  readStepParam,
  sourceEditable,
} from "./wizardSteps";
import SimilarHistorySection from "./SimilarHistorySection";
import { collectSimilarBulkTargets, resolveConfirmHsk } from "./similarApply";
import { buildSourceRows, normalizeHsk } from "./sourceRows";
import { resolveOrigin } from "./originToFta";
import { collectJudgmentHsks, judgeCandidate, rateSpread } from "./ratesJudge";

const CAP = 500; // 파일 업로드 상한 — DocuAI 핸드오프와 통일(스펙 2026-07-17)

let _gid = 0;
let imgPreviewWin = null; // 🔍 이미지 미리보기 창 핸들 — 재사용(2모니터에 둔 창을 그 자리에서 갱신)
// wb.rows[srcIdx][colIdx] 원본 값 읽기 — map 인덱스 미지정(-1/null)이면 빈 문자열
function srcVal(wb, srcIdx, colIdx) {
  if (colIdx == null || colIdx < 0 || srcIdx == null) return "";
  return String(wb?.rows?.[srcIdx]?.[colIdx] ?? "");
}
const COLS = [
  {
    dataField: "_name",
    headerText: "상품명",
    width: 240,
    style: "audit-grid-left",
  },
  {
    dataField: "_originHsk",
    headerText: "원본세번",
    width: 110,
    style: "audit-grid-mono",
  },
  {
    dataField: "_count",
    headerText: "건수",
    width: 56,
    labelFunction: (rowIndex, columnIndex, value, headerText, item) =>
      item?._count > 1 ? "×" + item._count : "",
  },
  {
    dataField: "_finalHsk",
    headerText: "신고세번",
    width: 120,
    style: "audit-grid-mono",
    // 수동수정=노란 강조 / 원본세번과 다르면 파란 좌테두리(참고 불일치 인지)
    styleFunction: (rowIndex, columnIndex, value, headerText, item) => {
      const cls = item?._edited
        ? "audit-grid-mono bulk-edited-cell"
        : "audit-grid-mono";
      const diff =
        item?._originHsk &&
        normalizeHsk(item._originHsk) !== normalizeHsk(item._finalHsk)
          ? " bulk-origin-diff"
          : "";
      return cls + diff;
    },
  },
  { dataField: "_rate", headerText: "세율", width: 80 },
  { dataField: "_fta", headerText: "FTA", width: 64 },
  {
    dataField: "_req",
    headerText: "요건",
    width: 64,
    styleFunction: (ri, ci, v, hv, item) =>
      item?._reqLevel === "customs"
        ? "bulk-req-customs"
        : item?._reqLevel === "notice"
          ? "bulk-req-notice"
          : "",
  },
  { dataField: "_confLabel", headerText: "신뢰도", width: 70 },
  { dataField: "status", headerText: "상태", width: 64 },
  {
    dataField: "_img",
    headerText: "이미지",
    width: 60,
    labelFunction: () => "🔍",
    cellMerge: false,
  },
];
export function toGridRows(
  results,
  wb,
  map,
  editedHsk,
  groups,
  expanded,
  jobItems,
  ratesMap,
  reqFlags,
) {
  const originIdx = detectOriginIdx(wb?.headers || []);
  const mk = (r, src, memberCount, members) => {
    const edited = editedHsk && editedHsk.get(src);
    const fin = edited || r.finalHsk || "";
    const originRaw = originIdx >= 0 ? srcVal(wb, src, originIdx) : "";
    const origin = resolveOrigin(originRaw);
    const j = judgeCandidate(fin, ratesMap, reqFlags, origin);
    return {
      ...r,
      _srcRow: src,
      _name:
        srcVal(wb, src, map?.nameIdx) || jobItems?.[r.idx]?.productName || "",
      _comp:
        srcVal(wb, src, map?.compIdx) || jobItems?.[r.idx]?.composition || "",
      _supplier:
        srcVal(wb, src, map?.supplierIdx) || jobItems?.[r.idx]?.supplier || "",
      _originHsk: srcVal(wb, src, map?.hsCodeIdx),
      _finalHsk: fin,
      _edited: edited ? 1 : 0,
      _confLabel: confidenceLabel(r.confidence),
      _crossLabel: crossLabel(r.cross),
      _masterLabel: masterLabel(r.masterMatch),
      _review: needsReview(r) ? 1 : 0,
      _count: memberCount,
      _members: members,
      _simHsk: r.simHsk || "",
      _simRate: r.simRate || 0,
      _origin: origin || "",
      _rate: j.rateText,
      _fta: j.ftaValue != null ? String(j.ftaValue) : j.ftaFallback,
      _req: j.reqText,
      _reqLevel: j.reqLevel || "",
    };
  };
  const out = [];
  (results || []).forEach((r) => {
    const g = groups?.[r.idx];
    const members = g ? g.members : [r.idx];
    // 건수(×N): 파일건=그룹 members 수, API건=서버 dedup이 센 원본 행수(items[].count)
    const cnt = g ? members.length : jobItems?.[r.idx]?.count || members.length;
    if (!expanded) {
      out.push(mk(r, members[0], cnt, members));
    } else {
      members.forEach((src) => out.push(mk(r, src, cnt, members)));
    }
  });
  return out;
}

// 공통 툴바 버튼 — primary(파랑)/secondary(회색 테두리)
function Btn({ primary, onClick, disabled, children, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "6px 14px",
        fontSize: 13,
        borderRadius: 6,
        cursor: disabled ? "default" : "pointer",
        border: primary ? "none" : "1px solid #d1d5db",
        background: disabled ? "#e5e7eb" : primary ? "#2563eb" : "#fff",
        color: disabled ? "#9ca3af" : primary ? "#fff" : "#374151",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function GroupItem({ active, onClick, label, warn, mono }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "5px 8px",
        cursor: "pointer",
        background: active ? "#dbeafe" : "transparent",
        color: warn ? "#b45309" : "#111827",
        fontFamily: mono ? "monospace" : "inherit",
        borderRadius: 4,
      }}
    >
      {label}
    </div>
  );
}

/** 대량 세번분류 패널 — 엑셀 업로드→교차검증 잡→그리드/상세→확정·다운로드. */
export default function BulkClassifyPanel({ embedded }) {
  const [wb, setWb] = useState(null); // {headers, rows, sheetName}
  const [map, setMap] = useState({
    nameIdx: -1,
    compIdx: -1,
    supplierIdx: -1,
    hsCodeIdx: -1,
  });
  const gridId = useMemo(() => `bulk-grid-${++_gid}`, []);
  const pvGridId = useMemo(() => `bulk-pv-grid-${++_gid}`, []); // 스텝3 미리보기 그리드(메인 그리드와 완전 별개)
  const pvHeadersRef = useRef([]); // pv 그리드 생성 시점 visible 원본 헤더(c{i} 인덱스↔헤더 복원용)
  const gridReady = useRef(false);
  const pollerRef = useRef(null);
  const [job, setJob] = useState(null); // {jobId,status,progress,results}
  const [jobStartMs, setJobStartMs] = useState(null); // 진행바 경과/예상 계산용 잡 시작시각
  const [selected, setSelected] = useState(null);
  const selectedRef = useRef(null); // setGridData 이펙트가 deps 없이 현재 선택을 읽어 유지 판단
  const [confirmed, setConfirmed] = useState(() => new Set());
  const [editedHsk, setEditedHsk] = useState(() => new Map()); // 수동수정(추후 태스크에서 채움)
  const [srcIndexes, setSrcIndexes] = useState([]); // job item idx(=유니크 idx) → 대표(rep) 원본 행 idx
  const [groups, setGroups] = useState([]); // [{keyword, members:[srcRow...]}]
  const [expanded, setExpanded] = useState(false); // false=중복제거 뷰, true=원본 전체 펼치기
  const [groupFilter, setGroupFilter] = useState(null); // null | hsk | "미분류" | "__review__"
  const [bulkHsk, setBulkHsk] = useState(""); // 체크행 일괄입력용 세번
  const [simThreshold, setSimThreshold] = useState("90"); // 유사 이력 일괄 반영 임계값(%)
  const [simBanner, setSimBanner] = useState("");
  const [listRefresh, setListRefresh] = useState(0); // 목록 수동 갱신 카운터
  const [sourceSettled, setSourceSettled] = useState(false); // 원본(source) 조회 완료 플래그 — 미보존 안내를 조회 끝난 뒤에만(로드 중 오표시 방지)
  const [step, setStep] = useState(STEP.SOURCE); // 3스텝 위저드 화면(잡 상태와 별개)
  const [ratesMap, setRatesMap] = useState(null); // 신고세번→세율요약. null=미조회/실패, {}=조회했으나 결과 없음
  const [ratesFailed, setRatesFailed] = useState(false); // rates-summary 조회 실패(다운로드는 막지 않고 경고만)
  const [reqFlags, setReqFlags] = useState({}); // hs4→{im,ex} 요건 배지(스텝2 그리드 + 스텝3 공유)
  const reqCacheRef = useRef(new Map()); // hs4→flags 세션 캐시 — 잡/스텝 전환에도 유지(실패 hs4는 미기록·재시도)
  const lawReqsCacheRef = useRef(new Map()); // 신고세번(10자리)→law_requirements 세션 캐시(성공만 기록 — 실패는 재시도 허용, reqCacheRef와 동일 관례)
  const [lawReqs, setLawReqs] = useState({ hsk: "", data: undefined }); // 요건 자세히 접이식 지연조회 — {hsk,data} 쌍. render는 hsk 일치 시에만 data 사용(행 전환으로 declaredHsk 바뀌면 자동으로 "불러오는 중" 취급 — 별도 리셋 이펙트 불요)
  const [layouts, setLayouts] = useState(null); // null=미로드, []=로드됨(빈/실패), [{id,name,columns,updatedAt}]
  const [selLayoutId, setSelLayoutId] = useState(""); // ""=기본(서버 미저장 개념)
  const [effCols, setEffCols] = useState(null); // 현재 표시 컬럼 [{header,visible}] — 드래그/칩/레이아웃 반영본
  const [newLayoutName, setNewLayoutName] = useState(""); // "새 이름으로 저장" 입력
  const [colChipsOpen, setColChipsOpen] = useState(false); // 컬럼 표시 칩 접이식

  // 스텝 변경 시 URL ?step= 반영(새로고침·딥링크 복원) — job 파라미터 있는 세션만 의미 있음
  const gotoStep = useCallback((s) => {
    setStep(s);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("step", s);
      window.history.replaceState(null, "", u.toString());
    } catch (e) {
      /* URL 미지원 환경 무시 */
    }
  }, []);

  const onFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const buf = await f.arrayBuffer();
    const parsed = readWorkbookRows(buf);
    setWb({ ...parsed, fileName: f.name });
    const dm = autoDetectColumns(parsed.headers);
    setMap({ ...dm, hsCodeIdx: detectHsCodeIdx(parsed.headers) });
    setEditedHsk(new Map()); // 새 파일 로드 시 이전 파일 행 인덱스 기준 수동수정값 무효화
    pollerRef.current?.stop();
    setJob(null);
    setJobStartMs(null);
    setSrcIndexes([]);
    setGroups([]);
    setExpanded(false);
    setSelected(null);
    setSourceSettled(false); // 파일 초안 — 원본 미보존 안내와 무관(job=null이라 미표시)
    setRatesMap(null);
    setRatesFailed(false);
    setReqFlags({});
    setSelLayoutId(""); // 새 파일 = 레이아웃 선택/미리보기 상태 리셋(effCols는 baseAoa 이펙트가 재설정)
    setNewLayoutName("");
    setColChipsOpen(false);
    setEffCols(null);
    setStep(STEP.SOURCE); // 새 파일 업로드 = 원본 스텝으로 복귀
  }, []);

  const preview = wb ? rowsToDedupItems(wb.rows, map, CAP) : null;

  // AUIGrid 생성/파기
  useEffect(() => {
    if (!window.AUIGrid) return;
    const sel = `#${gridId}`;
    if (window.AUIGrid.isCreated(sel)) window.AUIGrid.destroy(sel);
    window.AUIGrid.create(sel, COLS, {
      headerHeight: 32,
      rowHeight: 28,
      showRowNumColumn: true,
      enableSorting: true,
      enableFilter: true,
      selectionMode: "singleRow",
      showRowCheckColumn: true,
      // 검토필요 행 은은한 주황 배경 — rowStyleFunction 도 클래스명 반환(styleFunction과 동일 관례)
      rowStyleFunction: (rowIndex, item) =>
        item?._review ? "bulk-review-row" : "",
    });
    gridReady.current = true;
    // 행 선택 변경(방향키↑↓·클릭) → 우측 인스펙터 즉시 갱신(더블클릭 불요, 상시 패널).
    // 이 AUIGrid 빌드의 selectionChange 이벤트 인자에는 selectedItems/primeCell.item이 실려오지
    // 않는다(E2E 실측: 이벤트는 클릭·방향키 모두에서 발화하나 e.selectedItems 는 빈값 → 인스펙터
    // 미갱신). 그래서 이벤트 인자에 의존하지 말고, 권위 소스인 getSelectedIndex+getItemByRowIndex 로
    // 선택 행을 직접 읽는다(핸들러 내 동기 호출이 이미 갱신된 인덱스를 반환함도 실측). 클릭·키보드 공통.
    window.AUIGrid.bind(sel, "selectionChange", () => {
      try {
        const si = window.AUIGrid.getSelectedIndex(sel);
        const ri = Array.isArray(si) ? si[0] : si;
        if (ri != null && ri >= 0) {
          const it = window.AUIGrid.getItemByRowIndex(sel, ri);
          if (it) setSelected(it);
        }
      } catch (_) {
        /* 선택 읽기 실패 무시 — 다음 이벤트에서 재시도 */
      }
    });
    window.AUIGrid.bind(sel, "cellClick", (e) => {
      if (e?.dataField === "_img" && e?.item?._name) {
        // 창 "이름" 재사용은 Google이 window.name 을 지워 불안정 → JS 창 핸들을 붙잡아
        // 그 창을 그 자리에서(모니터2에 둔 위치 유지) 갱신한다. 닫혔으면 새로 연다.
        const imgUrl =
          "https://www.google.com/search?tbm=isch&q=" +
          encodeURIComponent(e.item._name);
        try {
          if (imgPreviewWin && !imgPreviewWin.closed) {
            imgPreviewWin.location.href = imgUrl; // 기존 창 그 자리에서 갱신
            imgPreviewWin.focus();
          } else {
            imgPreviewWin = window.open(
              imgUrl,
              "loginhsImgPreview",
              "popup,width=1100,height=850",
            );
          }
        } catch (_) {
          imgPreviewWin = window.open(
            imgUrl,
            "loginhsImgPreview",
            "popup,width=1100,height=850",
          );
        }
      }
    });
    return () => {
      if (window.AUIGrid?.isCreated(sel)) window.AUIGrid.destroy(sel);
      gridReady.current = false;
    };
  }, [gridId]);

  // 스텝 전환으로 그리드가 보이게 될 때 리사이즈 — 숨김(display:none) 상태에서 생성된
  // 그리드는 컨테이너 폭을 0으로 측정해 좁게 남는다(스텝1에서 생성→스텝2 전환 케이스).
  useEffect(() => {
    if (step !== STEP.CLASSIFY || !window.AUIGrid) return;
    const t = setTimeout(() => {
      try {
        if (window.AUIGrid.isCreated(`#${gridId}`)) {
          window.AUIGrid.resize(`#${gridId}`);
        }
      } catch (e) {
        /* 리사이즈 실패 무시 — 다음 전환에서 재시도 */
      }
    }, 0);
    return () => clearTimeout(t);
  }, [step, gridId]);

  // job.results → grid (groupFilter 클라이언트 필터 반영, 수동수정값 _finalHsk 기준)
  useEffect(() => {
    if (!gridReady.current || !window.AUIGrid || !job?.results) return;
    let rows = toGridRows(
      job.results,
      wb,
      map,
      editedHsk,
      groups,
      expanded,
      job?.items,
      ratesMap,
      reqFlags,
    );
    if (groupFilter === "__review__")
      rows = rows.filter((r) => r._review === 1);
    else if (groupFilter != null)
      rows = rows.filter((r) => (r._finalHsk || "미분류") === groupFilter);
    window.AUIGrid.setGridData(`#${gridId}`, rows);
    // 우측 인스펙터 선택 유지 — 현재 선택 행이 갱신된 rows에 남아있으면 그 행으로 재바인딩(editedHsk
    // 채택 등으로 데이터만 바뀌었을 때 첫 행으로 점프하지 않게). 없으면 첫 행 자동 선택(빈 화면 방지).
    const cur = selectedRef.current;
    const idx = cur
      ? rows.findIndex(
          (r) => r._srcRow === cur._srcRow && r.keyword === cur.keyword,
        )
      : -1;
    if (rows.length) {
      const pick = idx >= 0 ? idx : 0;
      try {
        window.AUIGrid.setSelectionByIndex(`#${gridId}`, pick);
      } catch (e) {
        /* 선택 실패 무시 */
      }
      setSelected(rows[pick]);
    } else {
      setSelected(null);
    }
  }, [
    job,
    gridId,
    groupFilter,
    wb,
    map,
    editedHsk,
    groups,
    expanded,
    ratesMap,
    reqFlags,
  ]);

  // 인스펙터 선택 유지 판단용 — selected 최신값을 ref에 미러(setGridData 이펙트 deps 오염 방지)
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  // 좌 패널 세번별 그룹 집계 — expanded=원본행 가중, 중복제거뷰=유니크 기준
  const grouping = useMemo(
    () => groupByHskV2(job?.results || [], groups, editedHsk, expanded),
    [job, groups, editedHsk, expanded],
  );

  useEffect(() => () => pollerRef.current?.stop(), []);

  const startJob = useCallback(async () => {
    // RECEIVED(분류 대기) 잡 — 서버가 이미 dedup·items 보유, start 만 쏜다(멱등).
    if (job?.status === "RECEIVED") {
      try {
        await startJobRun(job.jobId);
      } catch (e) {
        alert(e.message);
        return;
      }
      const total = job.itemCount || job?.items?.length || 0;
      setJob((prev) => ({
        ...prev,
        status: "PENDING",
        progress: { done: 0, total },
      }));
      setJobStartMs(Date.now());
      pollerRef.current?.stop();
      pollerRef.current = createJobPoller({
        jobId: job.jobId,
        getJob,
        onUpdate: setJob,
        onDone: setJob,
      });
      pollerRef.current.start();
      gotoStep(STEP.CLASSIFY);
      return;
    }
    const p = rowsToDedupItems(wb.rows, map, CAP);
    setGroups(p.groups);
    setSrcIndexes(p.groups.map((g) => g.members[0])); // rep(대표) srcRow — confirmAll·rep 조회 호환
    // 서버 저장용 items 인리치(새로고침·그룹원 열람 시 3필드 복원) + 파일명 label
    const enriched = p.items.map((it, i) => {
      const src = p.groups[i]?.members?.[0];
      return {
        ...it,
        productName: srcVal(wb, src, map?.nameIdx),
        composition: srcVal(wb, src, map?.compIdx),
        supplier: srcVal(wb, src, map?.supplierIdx),
        count: p.groups[i]?.members?.length || 1, // 원본 행수 — "유니크 N건(원본 M행)" 표기용
      };
    });
    const rawPayload =
      wb.rows.length <= 2000
        ? { headers: wb.headers, rows: wb.rows, map }
        : null; // 2,000행 초과 — 서버 보존 생략(세션 내 다운로드만, 현행과 동일)
    const { jobId } = await submitJob(enriched, {
      label: wb.fileName,
      ...(rawPayload ? { raw: rawPayload } : {}),
    });
    setJob({
      jobId,
      status: "PENDING",
      progress: { done: 0, total: p.used },
      results: [],
    });
    setJobStartMs(Date.now());
    pollerRef.current?.stop();
    pollerRef.current = createJobPoller({
      jobId,
      getJob,
      onUpdate: (j) => setJob(j),
      onDone: (j) => setJob(j),
    });
    pollerRef.current.start();
    gotoStep(STEP.CLASSIFY); // 분류 시작 → 세번분류 스텝으로 전환
  }, [wb, map, gotoStep, job]);

  // 목록/딥링크에서 기존 잡 열기 — wb 없는 API건/재조회 겸용. RUNNING 이면 폴러 재가동.
  const loadExistingJob = useCallback(async (jobId) => {
    pollerRef.current?.stop();
    setWb(null);
    setMap({ nameIdx: -1, compIdx: -1, supplierIdx: -1, hsCodeIdx: -1 });
    setGroups([]);
    setSrcIndexes([]);
    setEditedHsk(new Map());
    setSelected(null);
    setGroupFilter(null);
    setExpanded(false);
    setBulkHsk("");
    setConfirmed(new Set());
    setSourceSettled(false); // 원본 조회 시작 — 완료 전엔 미보존 안내 억제
    setRatesMap(null);
    setRatesFailed(false);
    setReqFlags({});
    setSelLayoutId(""); // 다른 잡 열기 = 레이아웃 선택/미리보기 리셋
    setNewLayoutName("");
    setColChipsOpen(false);
    setEffCols(null);
    try {
      const j = await getJob(jobId);
      setJob(j);
      setJobStartMs(Date.now());
      // 원본 복원(payload v2) — 파일/API 공통. 실패·구형 잡은 legacy 뷰(wb=null) 폴백.
      try {
        const raw = await getJobSource(jobId);
        if (raw && Array.isArray(raw.rows) && raw.map) {
          const vmap = {
            nameIdx: raw.map.nameIdx ?? 0,
            compIdx: raw.map.compIdx ?? -1,
            supplierIdx: raw.map.supplierIdx ?? -1,
            hsCodeIdx: detectHsCodeIdx(raw.headers || []),
          };
          setWb({
            headers: raw.headers || [],
            rows: raw.rows,
            fileName: j.label || "",
            virtual: true, // 서버 복원본 — 매핑 셀렉트 숨김·원본 읽기 전용
          });
          setMap(vmap);
          const p = rowsToDedupItems(raw.rows, vmap, CAP);
          setGroups(p.groups);
          setSrcIndexes(p.groups.map((g) => g.members[0]));
        }
      } catch (e) {
        /* 원본 없음 — legacy 뷰 */
      } finally {
        setSourceSettled(true); // 성공·실패·미보존 모두 — 이제 안내 판정 가능
      }
      // URL ?step= 딥링크가 우선 — 없을 때만 잡 상태 기본 스텝으로
      if (!readStepParam(window.location.search))
        setStep(defaultStepFor(j.status));
      if (j.status === "RUNNING" || j.status === "PENDING") {
        pollerRef.current = createJobPoller({
          jobId,
          getJob,
          onUpdate: setJob,
          onDone: setJob,
        });
        pollerRef.current.start();
      }
    } catch (e) {
      alert("잡을 불러올 수 없습니다: " + e.message);
    }
  }, []);

  // 잡/파일 상태를 비우고 목록으로 복귀
  const backToList = useCallback(() => {
    pollerRef.current?.stop();
    setJob(null);
    setWb(null);
    setMap({ nameIdx: -1, compIdx: -1, supplierIdx: -1, hsCodeIdx: -1 });
    setGroups([]);
    setSrcIndexes([]);
    setEditedHsk(new Map());
    setSelected(null);
    setGroupFilter(null);
    setExpanded(false);
    setBulkHsk("");
    setConfirmed(new Set());
    setSourceSettled(false); // 목록 복귀 = 원본 조회 상태도 리셋
    setRatesMap(null);
    setRatesFailed(false);
    setReqFlags({});
    setSelLayoutId(""); // 목록 복귀 = 레이아웃 선택/미리보기 리셋
    setNewLayoutName("");
    setColChipsOpen(false);
    setEffCols(null);
    setStep(STEP.SOURCE); // 목록 복귀 = 원본 스텝 리셋
    setListRefresh((v) => v + 1);
  }, []);

  // ?job=<id> 딥링크 — DocuAI 핸드오프에서 넘어온 진입. 마운트 시 1회.
  useEffect(() => {
    const jobParam = new URLSearchParams(window.location.search).get("job");
    if (jobParam) loadExistingJob(jobParam);
    const sp = readStepParam(window.location.search);
    if (sp) setStep(sp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 원본 행수 합(items[].count) — dedup 후 "유니크 N (원본 M행)" 표기. count 없는 구 잡=0(미표기)
  const origTotal = useMemo(
    () => (job?.items || []).reduce((s, it) => s + (Number(it?.count) || 0), 0),
    [job],
  );

  // job.results(idx=유니크 순번) → 그룹의 모든 원본 행 idx로 확장 매핑(중복행도 동일 결과 공유)
  const resultsBySrc = useMemo(
    () => resultsBySrcExpanded(job?.results || [], groups),
    [job, groups],
  );

  const onCancel = useCallback(async () => {
    if (job?.jobId) await cancelJob(job.jobId);
  }, [job]);

  const doConfirm = useCallback(
    async (row) => {
      const hsk = resolveConfirmHsk(row, editedHsk);
      if (!hsk) return;
      const ok = await confirmSeed(row.keyword, hsk, {
        productName: row._name,
        composition: row._comp,
        supplier: row._supplier,
      });
      if (ok) setConfirmed((s) => new Set(s).add(row.keyword));
    },
    [editedHsk],
  );

  // 인스펙터 "요건 자세히" 지연조회 — hsk별 세션 캐시 적중 시 재호출 없음(실패는 캐시 미기록·재시도).
  const loadLawReqs = useCallback((hsk) => {
    const cached = lawReqsCacheRef.current.get(hsk);
    if (cached !== undefined) {
      setLawReqs({ hsk, data: cached });
      return;
    }
    setLawReqs({ hsk, data: undefined }); // 불러오는 중…
    getHskDetail(hsk)
      .then((payload) => {
        const data = payload ? payload.law_requirements || [] : null;
        if (data !== null) lawReqsCacheRef.current.set(hsk, data);
        setLawReqs((prev) => (prev.hsk === hsk ? { hsk, data } : prev));
      })
      // fetch는 네트워크 단절/재시작 시 reject — 미처리면 "불러오는 중" 고착(최종리뷰 Important).
      .catch(() =>
        setLawReqs((prev) => (prev.hsk === hsk ? { hsk, data: null } : prev)),
      );
  }, []);

  // 체크된 그리드 행들의 최종세번을 일괄로 수동수정(editedHsk)에 반영
  const applyBulkHsk = useCallback(() => {
    const hsk = bulkHsk.replace(/\s/g, "");
    if (!hsk) return;
    const checked = window.AUIGrid?.getCheckedRowItems(`#${gridId}`) || [];
    if (!checked.length) {
      alert("체크된 행이 없습니다.");
      return;
    }
    setEditedHsk((prev) => {
      const m = new Map(prev);
      checked.forEach((c) => {
        const members =
          c?.item?._members ||
          (c?.item?._srcRow != null ? [c.item._srcRow] : []);
        members.forEach((src) => m.set(src, hsk));
      });
      return m;
    });
  }, [bulkHsk, gridId]);

  // 임계값 이상 유사 이력 세번 일괄 반영 — 체크행 우선, 없으면 전체. editedHsk 트랙(확정 저장 별도).
  const applySimilarBulk = useCallback(() => {
    setSimBanner("");
    const th = parseInt(simThreshold, 10);
    if (!Number.isFinite(th) || th < 40 || th > 100) {
      setSimBanner("임계값은 40~100 사이 숫자여야 합니다");
      return;
    }
    const checked = window.AUIGrid?.getCheckedRowItems(`#${gridId}`) || [];
    const rows = checked.length
      ? checked.map((c) => c.item).filter(Boolean)
      : window.AUIGrid?.getGridData(`#${gridId}`) || [];
    const targets = collectSimilarBulkTargets(rows, th);
    if (!targets.length) {
      setSimBanner(`반영 대상이 없습니다(일치율 ${th}%↑)`);
      return;
    }
    setEditedHsk((prev) => {
      const m = new Map(prev);
      targets.forEach((t) => m.set(t.src, t.hsk));
      return m;
    });
    setSimBanner(`${targets.length}건 반영`);
  }, [simThreshold, gridId]);

  const confirmAll = useCallback(async () => {
    for (const r of job?.results || []) {
      if (r.status !== "ok") continue;
      // 순차(백엔드 부담 최소) — 소량이므로 충분
      // API건(loadExistingJob)은 srcIndexes가 비어 있음 → r.idx 폴백(toGridRows/applyBulkHsk의 editedHsk 키와 정합)
      const src = srcIndexes[r.idx] ?? r.idx;
      const hsk = editedHsk.get(src) || r.finalHsk;
      const it = job?.items?.[r.idx];
      const extra = {
        productName: srcVal(wb, src, map?.nameIdx) || it?.productName || "",
        composition: srcVal(wb, src, map?.compIdx) || it?.composition || "",
        supplier: srcVal(wb, src, map?.supplierIdx) || it?.supplier || "",
      };
      if (hsk && (await confirmSeed(r.keyword, hsk, extra))) {
        setConfirmed((s) => new Set(s).add(r.keyword));
      }
    }
  }, [job, srcIndexes, editedHsk, wb, map]);

  const hasErrors = (job?.results || []).some((r) => r.status === "error");

  const onRetry = useCallback(async () => {
    if (!job?.jobId) return;
    await retryFailed(job.jobId);
    // 재분류 후 최신 상태 재폴링(폴러 재가동)
    pollerRef.current?.stop();
    pollerRef.current = createJobPoller({
      jobId: job.jobId,
      getJob,
      onUpdate: setJob,
      onDone: setJob,
    });
    pollerRef.current.start();
  }, [job]);

  // 판정 대상 hsk 수집(신고세번 우선 + 후보 union + 원본세번) — 세율/요건 두 이펙트가 공유하는 순수 계산.
  const collectPanelJudgmentHsks = useCallback(() => {
    const hsCodeIdx = map?.hsCodeIdx ?? -1;
    const originExtras =
      hsCodeIdx >= 0
        ? (job?.results || []).map((r) => {
            const rep = groups?.[r.idx]?.members?.[0] ?? r.idx;
            return normalizeHsk(srcVal(wb, rep, hsCodeIdx));
          })
        : [];
    return collectJudgmentHsks(
      job?.results || [],
      groups,
      editedHsk,
      originExtras,
    );
  }, [job, groups, editedHsk, wb, map]);

  // 잡 DONE 시 신고세번+후보 세율 요약 프리페치(codes 포함, fta=1) — 스텝2 그리드·스텝3 다운로드 공유.
  // 실패=공란 출력+경고(다운로드는 막지 않음). 이전엔 스텝3 진입 게이트였으나 스텝2에서도 배지 표시 위해 리프트.
  useEffect(() => {
    if (job?.status !== "DONE" || !wb) return;
    const hsks = collectPanelJudgmentHsks();
    if (!hsks.length) {
      setRatesMap({});
      setRatesFailed(false);
      return;
    }
    let alive = true;
    getRatesSummary(hsks, { fta: true })
      .then((m) => {
        if (!alive) return;
        setRatesMap(m || {});
        setRatesFailed(m == null);
      })
      .catch(() => {
        if (!alive) return;
        setRatesMap({});
        setRatesFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [job, wb, collectPanelJudgmentHsks]);

  // 잡 DONE 시 hsks의 distinct hs4 요건(수입공고/세관장확인) 프리페치 — 세션 캐시(reqCacheRef)로
  // 이미 조회한 hs4는 재요청 없이 재사용. 4개씩 청크 병렬 조회, 실패 hs4는 캐시 미기록(다음 실행 재시도).
  useEffect(() => {
    if (job?.status !== "DONE" || !wb) return;
    const hsks = collectPanelJudgmentHsks();
    const hs4s = Array.from(
      new Set(hsks.map((h) => h.slice(0, 4)).filter((h) => h.length === 4)),
    );
    const mergeCached = () => {
      const merged = {};
      hs4s.forEach((h4) =>
        Object.assign(merged, reqCacheRef.current.get(h4) || {}),
      );
      return merged;
    };
    const need = hs4s.filter((h4) => !reqCacheRef.current.has(h4));
    if (!need.length) {
      setReqFlags(mergeCached());
      return;
    }
    let alive = true;
    (async () => {
      for (let i = 0; i < need.length; i += 4) {
        const chunk = need.slice(i, i + 4);
        const settled = await Promise.all(
          chunk.map((h4) =>
            getReqFlags(h4)
              .then((flags) => ({ h4, flags }))
              .catch(() => ({ h4, flags: null })),
          ),
        );
        if (!alive) return;
        settled.forEach(({ h4, flags }) => {
          if (flags) reqCacheRef.current.set(h4, flags); // 실패(null)=미기록 — 다음 실행 재시도
        });
      }
      if (!alive) return;
      setReqFlags(mergeCached());
    })();
    return () => {
      alive = false;
    };
  }, [job, wb, collectPanelJudgmentHsks]);

  // 스텝3 출력 기준 AoA(0행=헤더) — 기존 onDownload 내부 buildExportAoa를 useMemo로 승격.
  // editedHsk·rates 변경 시 재계산되고, 그때 effCols 순서·visible을 보존하며 재merge(아래 이펙트).
  const baseAoa = useMemo(
    () =>
      job?.status === "DONE" && wb
        ? buildExportAoa({
            headers: wb.headers,
            rows: wb.rows,
            map,
            resultsBySrc,
            editedHsk,
            ratesByHsk: ratesMap || {},
          })
        : null,
    [job, wb, map, resultsBySrc, editedHsk, ratesMap],
  );

  // 스텝3 진입 시 개인 레이아웃 목록 1회 로드(실패 시 [] — 기본만 사용).
  useEffect(() => {
    if (step !== STEP.EXPORT || layouts !== null) return;
    let alive = true;
    listExportLayouts().then((ls) => {
      if (alive) setLayouts(ls || []);
    });
    return () => {
      alive = false;
    };
  }, [step, layouts]);

  // baseAoa 확정/변경 시 effCols 동기화 — prev(현재 순서·visible)를 보존하며 재merge.
  // 최초(prev=null)=기본 전체 visible. baseAoa 소멸 시 null.
  useEffect(() => {
    if (!baseAoa) {
      setEffCols(null);
      return;
    }
    setEffCols((prev) => mergeLayout(baseAoa[0], prev));
  }, [baseAoa]);

  // 드래그 후 실제 컬럼 순서 — AUIGrid.getColumnLayout 실측 파싱(형태 상이 대비 방어).
  // headerText는 XSS 이스케이프되어 effCols.header와 불일치 → dataField(c{i}) 인덱스로
  // 생성 시점 원본 헤더(pvHeadersRef)를 복원(중복 헤더 모호성도 해소). 실패=null 폴백.
  const readGridOrder = useCallback(() => {
    try {
      const layout = window.AUIGrid.getColumnLayout(`#${pvGridId}`);
      if (!Array.isArray(layout)) return null;
      const headers = pvHeadersRef.current || [];
      const out = [];
      layout.forEach((c) => {
        const df = c && typeof c === "object" ? c.dataField : null;
        const m = typeof df === "string" ? df.match(/^c(\d+)$/) : null;
        if (!m) return; // rownum 등 특수열·비 c{i} 무시
        const h = headers[Number(m[1])];
        if (h != null) out.push(String(h));
      });
      return out.length ? out : null;
    } catch (e) {
      return null;
    }
  }, [pvGridId]);

  // 저장·다운로드용 최종 컬럼 목록 — 그리드 드래그 순서(표시)를 반영하고 hidden은 뒤에 보존.
  const currentColumns = useCallback(() => {
    const order = readGridOrder();
    if (!order) return effCols || [];
    return order
      .map((h) => ({ header: h, visible: true }))
      .concat((effCols || []).filter((c) => !c.visible));
  }, [readGridOrder, effCols]);

  // 상태 갱신 전에 현재 드래그 순서를 effCols에 접기(칩 토글 시 순서 유실 방지).
  const syncOrderThen = useCallback(
    (update) => {
      const order = readGridOrder();
      setEffCols((prev) => {
        const base = order
          ? order
              .map((h) => ({ header: h, visible: true }))
              .concat((prev || []).filter((c) => !c.visible))
          : prev || [];
        return update(base);
      });
    },
    [readGridOrder],
  );

  const toggleCol = useCallback(
    (h) => {
      const target = (effCols || []).find((c) => c.header === h);
      const visibleCount = (effCols || []).filter(
        (c) => c.visible !== false,
      ).length;
      // 마지막 남은 표시 컬럼을 숨기려는 경우 무시(해제 불가) — 빈 columnDefs 크래시·빈 xlsx 가드.
      // visible→hidden 전환일 때만 차단, hidden→visible은 항상 허용.
      if (visibleCount <= 1 && target && target.visible !== false) return;
      syncOrderThen((cols) =>
        cols.map((c) => (c.header === h ? { ...c, visible: !c.visible } : c)),
      );
    },
    [effCols, syncOrderThen],
  );

  // 미리보기 그리드 생성/갱신 — 스텝3에서 보이는 상태로 조건부 렌더된 div에 create.
  // visible 컬럼만 사영 → columnDefs 구성이 바뀌므로 재생성(미리보기라 이벤트 바인딩 없음·저비용).
  useEffect(() => {
    if (!window.AUIGrid) return;
    const sel = `#${pvGridId}`;
    if (step !== STEP.EXPORT || !baseAoa || !effCols) {
      if (window.AUIGrid.isCreated(sel)) window.AUIGrid.destroy(sel);
      return;
    }
    const projected = projectAoa(baseAoa, effCols);
    pvHeadersRef.current = projected[0] || []; // 원본(비이스케이프) visible 헤더 — c{i}↔헤더 복원
    const { columnDefs, rows } = aoaToGridData(projected);
    if (window.AUIGrid.isCreated(sel)) window.AUIGrid.destroy(sel);
    window.AUIGrid.create(sel, columnDefs, {
      headerHeight: 32,
      rowHeight: 28,
      enableMovingColumn: true,
      showRowNumColumn: true,
      enableFilter: false,
      editable: false,
    });
    window.AUIGrid.setGridData(sel, rows);
    return () => {
      if (window.AUIGrid?.isCreated(sel)) window.AUIGrid.destroy(sel);
    };
  }, [step, baseAoa, effCols, pvGridId]);

  // 레이아웃 선택 변경 — 선택분(또는 기본)으로 effCols 리셋(드래그/칩 상태 폐기는 의도).
  const onSelectLayout = useCallback(
    (id) => {
      setSelLayoutId(id);
      setNewLayoutName("");
      if (!baseAoa) return;
      const sel = (layouts || []).find((l) => String(l.id) === id);
      setEffCols(mergeLayout(baseAoa[0], sel ? sel.columns : null));
    },
    [layouts, baseAoa],
  );

  const reloadLayouts = useCallback(async () => {
    const ls = await listExportLayouts();
    setLayouts(ls || []);
  }, []);

  // 선택 레이아웃 덮어쓰기 저장(이름 유지) — 현재 드래그 순서·visible을 그대로 저장·상태 고정.
  const onSaveLayout = useCallback(async () => {
    const sel = (layouts || []).find((l) => String(l.id) === selLayoutId);
    if (!sel) return;
    const cols = currentColumns();
    try {
      await saveExportLayout(sel.name, cols);
      setEffCols(cols); // 드래그 순서를 상태에 고정(재생성 시 유지)
      await reloadLayouts();
    } catch (e) {
      alert(e.message);
    }
  }, [layouts, selLayoutId, currentColumns, reloadLayouts]);

  // 새 이름으로 저장 — 동명 존재 시 확인 후 덮어쓰기. 성공 시 그 레이아웃을 선택.
  const onSaveAsLayout = useCallback(async () => {
    const name = newLayoutName.trim();
    if (!name) return;
    const dup = (layouts || []).some((l) => l.name === name);
    if (
      dup &&
      !window.confirm(`"${name}" 레이아웃이 이미 있습니다. 덮어쓸까요?`)
    )
      return;
    const cols = currentColumns();
    try {
      const res = await saveExportLayout(name, cols);
      setEffCols(cols);
      await reloadLayouts();
      if (res && res.id != null) setSelLayoutId(String(res.id));
      setNewLayoutName("");
    } catch (e) {
      alert(e.message);
    }
  }, [newLayoutName, layouts, currentColumns, reloadLayouts]);

  // 선택 레이아웃 삭제 — 확인 후 삭제·기본으로 복귀.
  const onDeleteLayout = useCallback(async () => {
    const sel = (layouts || []).find((l) => String(l.id) === selLayoutId);
    if (!sel) return;
    if (!window.confirm(`"${sel.name}" 레이아웃을 삭제할까요?`)) return;
    try {
      const ok = await deleteExportLayout(sel.id);
      if (!ok) {
        alert("레이아웃을 삭제하지 못했습니다.");
        return;
      }
      await reloadLayouts();
      setSelLayoutId(""); // 기본 복귀
      if (baseAoa) setEffCols(mergeLayout(baseAoa[0], null));
    } catch (e) {
      alert(e.message);
    }
  }, [layouts, selLayoutId, reloadLayouts, baseAoa]);

  const onDownload = useCallback(() => {
    if (!wb || !baseAoa) return;
    // 현재 그리드 순서·표시 컬럼을 그대로 반영(readGridOrder→effCols 폴백).
    const finalAoa = projectAoa(baseAoa, currentColumns());
    const wb2 = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb2,
      XLSX.utils.aoa_to_sheet(finalAoa),
      "결과",
    );
    const stem = (wb.fileName || job?.label || "대량세번분류").replace(
      /\.[^.]+$/,
      "",
    );
    XLSX.writeFile(wb2, `${stem}_loginhs.xlsx`);
  }, [wb, baseAoa, currentColumns, job]);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        position: "relative",
      }}
    >
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
        <h2
          style={{
            margin: 0,
            fontSize: 18,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          대량 세번분류
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "#9ca3af",
              border: "1px solid #d1d5db",
              borderRadius: 4,
              padding: "0 5px",
              lineHeight: "16px",
            }}
          >
            beta
          </span>
        </h2>
      </div>
      {(wb || job) && (
        <StepTabs
          step={step}
          jobStatus={job?.status || null}
          hasSource={!!wb}
          onChange={gotoStep}
        />
      )}
      <div style={{ flex: 1, padding: 20, overflow: "auto" }}>
        {!job && (
          <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} />
        )}
        {!wb && !job && (
          <BulkJobList onOpen={loadExistingJob} refreshKey={listRefresh} />
        )}
        {step === STEP.SOURCE && wb && (
          <div style={{ marginTop: 16 }}>
            {sourceEditable(job?.status || null) && !wb.virtual && (
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <label>
                  상품명&nbsp;
                  <select
                    value={map.nameIdx}
                    onChange={(e) =>
                      setMap((m) => ({ ...m, nameIdx: +e.target.value }))
                    }
                  >
                    {wb.headers.map((h, i) => (
                      <option key={i} value={i}>
                        {String(h || `열${i + 1}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  성분(선택)&nbsp;
                  <select
                    value={map.compIdx}
                    onChange={(e) =>
                      setMap((m) => ({ ...m, compIdx: +e.target.value }))
                    }
                  >
                    <option value={-1}>(없음)</option>
                    {wb.headers.map((h, i) => (
                      <option key={i} value={i}>
                        {String(h || `열${i + 1}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  해외공급자(선택)&nbsp;
                  <select
                    value={map.supplierIdx}
                    onChange={(e) =>
                      setMap((m) => ({ ...m, supplierIdx: +e.target.value }))
                    }
                  >
                    <option value={-1}>(없음)</option>
                    {wb.headers.map((h, i) => (
                      <option key={i} value={i}>
                        {String(h || `열${i + 1}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            <div
              style={{
                maxHeight: 320,
                overflow: "auto",
                border: "1px solid #e5e7eb",
              }}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  fontSize: 12,
                  width: "max-content",
                }}
              >
                <thead>
                  <tr>
                    {wb.headers.map((h, i) => (
                      <th
                        key={i}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "2px 8px",
                          background: i === map.nameIdx ? "#dbeafe" : "#f9fafb",
                        }}
                      >
                        {String(h || `열${i + 1}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {wb.rows.map((r, ri) => (
                    <tr key={ri}>
                      {wb.headers.map((_, ci) => (
                        <td
                          key={ci}
                          style={{
                            border: "1px solid #e5e7eb",
                            padding: "2px 8px",
                          }}
                        >
                          {String(r?.[ci] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 12, fontSize: 13 }}>
              유효 품명 <b>{preview.used}</b>건(원본 {preview.totalRows}행)
              {preview.capped && (
                <span style={{ color: "#b45309", marginLeft: 8 }}>
                  ⚠ 유니크 {CAP}건 초과 — 상위 {CAP}건만 처리됩니다.
                </span>
              )}
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <Btn
                primary
                onClick={startJob}
                disabled={job ? job.status !== "RECEIVED" : !preview?.used}
              >
                분류 시작
              </Btn>
              {!job && <Btn onClick={backToList}>목록으로</Btn>}
            </div>
            {job?.status === "RECEIVED" && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                DocuAI에서 전송된 원본입니다(수정은 DocuAI에서 재전송). 내용
                확인 후 [분류 시작]을 누르세요.
              </div>
            )}
          </div>
        )}
        {step === STEP.SOURCE && job?.status === "RECEIVED" && (
          <div style={{ marginTop: 12 }}>
            <Btn onClick={backToList}>목록으로</Btn>
          </div>
        )}
        {step === STEP.SOURCE && !wb && job && sourceSettled && (
          <div style={{ marginTop: 16, fontSize: 13, color: "#6b7280" }}>
            이 이력은 원본 전체행이 저장되지 않아 원본 보기·다운로드를 제공하지
            않습니다.
          </div>
        )}
        <div
          style={{
            marginTop: 20,
            display: step === STEP.CLASSIFY ? "block" : "none",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Btn onClick={confirmAll} disabled={job?.status !== "DONE"}>
              전체 확정 저장
            </Btn>
            {job && wb && (
              <Btn onClick={() => setExpanded((v) => !v)}>
                {expanded ? "중복제거 보기" : "전체 펼치기"}
              </Btn>
            )}
            {job?.status === "DONE" && hasErrors && (
              <Btn onClick={onRetry}>실패행 재분류</Btn>
            )}
            {job?.status === "DONE" && (
              <Btn onClick={() => gotoStep(STEP.EXPORT)} disabled={!wb}>
                다운로드 단계로
              </Btn>
            )}
            {job?.status === "DONE" && (
              <span
                style={{
                  display: "inline-flex",
                  gap: 6,
                  alignItems: "center",
                  marginLeft: 12,
                }}
              >
                <input
                  value={bulkHsk}
                  onChange={(e) => setBulkHsk(e.target.value)}
                  placeholder="세번(10자리)"
                  style={{
                    width: 120,
                    padding: "5px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontFamily: "monospace",
                  }}
                />
                <Btn onClick={applyBulkHsk}>체크행에 입력</Btn>
              </span>
            )}
            {job?.status === "DONE" && (
              <span
                style={{
                  display: "inline-flex",
                  gap: 6,
                  alignItems: "center",
                  marginLeft: 12,
                }}
              >
                일치율
                <input
                  value={simThreshold}
                  onChange={(e) => setSimThreshold(e.target.value)}
                  style={{
                    width: 48,
                    padding: "5px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    textAlign: "right",
                  }}
                />
                %↑
                <Btn onClick={applySimilarBulk}>이력 반영</Btn>
                {simBanner && (
                  <span style={{ fontSize: 12, color: "#92400e" }}>
                    {simBanner}
                  </span>
                )}
              </span>
            )}
            {(job || wb) && <Btn onClick={backToList}>목록으로</Btn>}
            {job && (
              <span
                style={{
                  display: "inline-flex",
                  gap: 8,
                  alignItems: "center",
                  marginLeft: 12,
                  fontSize: 13,
                }}
              >
                {job.status !== "RUNNING" && (
                  <>
                    <span>
                      {job.progress?.done ?? 0}/{job.progress?.total ?? 0}
                    </span>
                    {origTotal > (job.progress?.total ?? 0) && (
                      <span style={{ color: "#6b7280" }}>
                        (원본 {origTotal}행)
                      </span>
                    )}
                    <span style={{ color: "#6b7280" }}>{job.status}</span>
                  </>
                )}
                {(job.status === "RUNNING" || job.status === "PENDING") && (
                  <Btn onClick={onCancel}>취소</Btn>
                )}
              </span>
            )}
          </div>
          {job &&
            job.status === "RUNNING" &&
            (() => {
              const { done = 0, total = 0 } = job.progress || {};
              const p = formatProgress(
                done,
                total,
                jobStartMs ? Date.now() - jobStartMs : 0,
              );
              return (
                <div className="bulk-progress">
                  <div className="bulk-progress-head">
                    분류 중… {p.pct}% · {p.doneText} 완료 · {p.elapsedText}
                    {p.etaText ? ` · ${p.etaText}` : ""}
                  </div>
                  <div className="bulk-progress-track">
                    <div
                      className="bulk-progress-fill"
                      style={{ width: `${p.pct}%` }}
                    />
                  </div>
                </div>
              );
            })()}
          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
            <div
              style={{
                width: 180,
                flexShrink: 0,
                borderRight: "1px solid #e5e7eb",
                overflow: "auto",
                maxHeight: 420,
                fontSize: 13,
              }}
            >
              <GroupItem
                active={groupFilter === null}
                onClick={() => setGroupFilter(null)}
                label={`전체 ${grouping.total}`}
              />
              <GroupItem
                active={groupFilter === "__review__"}
                onClick={() => setGroupFilter("__review__")}
                label={`검토필요 ${grouping.reviewCount}`}
                warn
              />
              <div
                style={{ borderTop: "1px solid #f0f0f0", margin: "4px 0" }}
              />
              {grouping.groups.map((g) => (
                <GroupItem
                  key={g.hsk}
                  active={groupFilter === g.hsk}
                  onClick={() => setGroupFilter(g.hsk)}
                  label={`${g.hsk}  (${g.count})`}
                  mono
                />
              ))}
            </div>
            <div id={gridId} style={{ flex: 1, height: 420, minWidth: 0 }} />
            {/* 우측 상시 인스펙터 — 그리드 방향키/클릭 행이동 시 즉시 갱신(더블클릭 불요) */}
            <div
              style={{
                width: 380,
                flexShrink: 0,
                alignSelf: "flex-start",
                borderLeft: "1px solid #e5e7eb",
                minHeight: 420,
                paddingLeft: 12,
              }}
            >
              {selected ? (
                (() => {
                  // 인스펙터 1회 계산 — 신고세번(정규화)·후보 판정 배열·세율 갈림·요건 자세히 게이트.
                  const declaredHsk = normalizeHsk(
                    resolveConfirmHsk(selected, editedHsk),
                  );
                  const judged = buildSourceRows(selected)
                    .filter((s) => s.applicable)
                    .map((s) =>
                      judgeCandidate(
                        normalizeHsk(s.hsk),
                        ratesMap,
                        reqFlags,
                        selected._origin || null,
                      ),
                    );
                  const spread = rateSpread(judged);
                  const declaredReqIm =
                    (reqFlags &&
                      reqFlags[declaredHsk] &&
                      reqFlags[declaredHsk].im) ||
                    null;
                  const showLawReqs =
                    declaredHsk.length === 10 && !!declaredReqIm;
                  const lawData =
                    lawReqs.hsk === declaredHsk ? lawReqs.data : undefined;
                  return (
                    <div>
                      <h3 style={{ marginTop: 0, fontSize: 15 }}>
                        {selected.keyword}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          margin: "8px 0 12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ color: "#6b7280", fontSize: 13 }}>
                          신고세번
                        </span>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: 16,
                            fontWeight: 700,
                          }}
                        >
                          {declaredHsk || "-"}
                        </span>
                        {declaredHsk.length === 10 && (
                          <button
                            onClick={() =>
                              window.open(
                                `/?audit=quickref&qhs=${declaredHsk.slice(0, 4)}`,
                                "_blank",
                              )
                            }
                            title="속견표에서 보기"
                            style={{
                              fontSize: 12,
                              padding: "1px 6px",
                              border: "1px solid #d1d5db",
                              borderRadius: 6,
                              background: "#fff",
                              color: "#2563eb",
                              cursor: "pointer",
                            }}
                          >
                            ↗
                          </button>
                        )}
                        {spread && (
                          <span
                            style={{
                              color: "#b45309",
                              fontWeight: 600,
                              fontSize: 12,
                            }}
                          >
                            세율 갈림 {spread.min}~{spread.max}%
                          </span>
                        )}
                      </div>
                      <table
                        style={{
                          fontSize: 13,
                          width: "100%",
                          borderCollapse: "collapse",
                        }}
                      >
                        <tbody>
                          {buildSourceRows(selected).map((s) => {
                            // 현재 신고세번과 정규화 비교해 불일치 소스 세번을 색 강조(스펙 §5)
                            const mismatch =
                              s.hsk &&
                              declaredHsk &&
                              normalizeHsk(s.hsk) !== declaredHsk;
                            const cand = normalizeHsk(s.hsk);
                            const j =
                              cand.length === 10
                                ? judgeCandidate(
                                    cand,
                                    ratesMap,
                                    reqFlags,
                                    selected._origin || null,
                                  )
                                : null;
                            const ftaText = !j
                              ? ""
                              : j.ftaValue != null
                                ? `FTA ${j.ftaValue}${
                                    selected._origin
                                      ? ` (${selected._origin})`
                                      : ""
                                  }`
                                : j.ftaFallback
                                  ? `FTA ${j.ftaFallback}`
                                  : "";
                            const parts = j
                              ? [
                                  j.rateText ? { text: j.rateText } : null,
                                  ftaText ? { text: ftaText } : null,
                                  j.reqText
                                    ? {
                                        text: j.reqText,
                                        color:
                                          j.reqLevel === "customs"
                                            ? "#b91c1c"
                                            : "#b45309",
                                      }
                                    : null,
                                ].filter(Boolean)
                              : [];
                            return (
                              <tr
                                key={s.key}
                                style={{ borderTop: "1px solid #f0f0f0" }}
                              >
                                <td
                                  style={{
                                    padding: "5px 6px",
                                    color: "#6b7280",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {s.label}
                                </td>
                                <td
                                  style={{
                                    padding: "5px 6px",
                                    fontFamily: "monospace",
                                    color: mismatch ? "#b45309" : undefined,
                                  }}
                                >
                                  {s.hsk || "—"}
                                  {parts.length > 0 && (
                                    <span
                                      style={{
                                        display: "block",
                                        fontFamily: "inherit",
                                        fontSize: 11,
                                        marginTop: 2,
                                      }}
                                    >
                                      {parts.map((p, i) => (
                                        <React.Fragment key={i}>
                                          {i > 0 && (
                                            <span style={{ color: "#d1d5db" }}>
                                              {" "}
                                              ·{" "}
                                            </span>
                                          )}
                                          <span
                                            style={{
                                              color: p.color || "#6b7280",
                                            }}
                                          >
                                            {p.text}
                                          </span>
                                        </React.Fragment>
                                      ))}
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "5px 6px",
                                    color: "#9ca3af",
                                    fontSize: 12,
                                  }}
                                >
                                  {s.note}
                                </td>
                                <td
                                  style={{
                                    padding: "5px 6px",
                                    textAlign: "right",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {cand.length === 10 && (
                                    <button
                                      onClick={() =>
                                        window.open(
                                          `/?audit=quickref&qhs=${cand.slice(0, 4)}`,
                                          "_blank",
                                        )
                                      }
                                      title="속견표에서 보기"
                                      style={{
                                        fontSize: 12,
                                        padding: "2px 6px",
                                        border: "none",
                                        background: "transparent",
                                        color: "#2563eb",
                                        cursor: "pointer",
                                        marginRight: s.applicable ? 2 : 0,
                                      }}
                                    >
                                      ↗
                                    </button>
                                  )}
                                  {s.applicable && (
                                    <button
                                      onClick={() => {
                                        const members =
                                          selected._members ||
                                          (selected._srcRow != null
                                            ? [selected._srcRow]
                                            : []);
                                        setEditedHsk((prev) => {
                                          const m = new Map(prev);
                                          // 원격지 포맷 방어 — 정규화 10자리로 세팅(member 전체 균일)
                                          members.forEach((src) =>
                                            m.set(src, normalizeHsk(s.hsk)),
                                          );
                                          return m;
                                        });
                                      }}
                                      style={{
                                        fontSize: 12,
                                        padding: "2px 8px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: 6,
                                        background: "#fff",
                                        cursor: "pointer",
                                      }}
                                    >
                                      이 세번
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <details style={{ marginTop: 12 }}>
                        <summary
                          style={{
                            cursor: "pointer",
                            fontSize: 13,
                            color: "#374151",
                          }}
                        >
                          근거 자세히
                        </summary>
                        <div style={{ marginTop: 8, fontSize: 12 }}>
                          <div style={{ color: "#6b7280" }}>
                            판정근거: {selected.decisionLabel || "-"} · 교차합의{" "}
                            {confidenceLabel(selected.confidence)} (
                            {crossLabel(selected.cross)})
                          </div>
                          {(selected.topCandidates ?? []).length > 0 && (
                            <div style={{ marginTop: 6 }}>
                              <b>top 후보</b>
                              <ul style={{ margin: "4px 0" }}>
                                {(selected.topCandidates ?? []).map((c, i) => (
                                  <li
                                    key={i}
                                    style={{ fontFamily: "monospace" }}
                                  >
                                    {c.hsk ||
                                      c.hsk10 ||
                                      JSON.stringify(c).slice(0, 60)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <SimilarHistorySection
                            keyword={selected.keyword}
                            onApply={(hsk) => {
                              const members =
                                selected._members ||
                                (selected._srcRow != null
                                  ? [selected._srcRow]
                                  : []);
                              setEditedHsk((prev) => {
                                const m = new Map(prev);
                                members.forEach((src) => m.set(src, hsk));
                                return m;
                              });
                            }}
                          />
                        </div>
                      </details>
                      {showLawReqs && (
                        <details
                          // key=신고세번 — 행 전환 시 리마운트(자동 접힘). 열어둔 채 방향키 이동하면
                          // 비제어 <details>의 onToggle이 재발화하지 않아 "불러오는 중" 고착(리뷰 Important).
                          key={declaredHsk}
                          style={{ marginTop: 8 }}
                          onToggle={(e) =>
                            e.currentTarget.open && loadLawReqs(declaredHsk)
                          }
                        >
                          <summary
                            style={{
                              cursor: "pointer",
                              fontSize: 13,
                              color: "#374151",
                            }}
                          >
                            요건 자세히
                            {declaredReqIm === "customs" ? " · 세관장확인" : ""}
                          </summary>
                          <div style={{ marginTop: 8, fontSize: 12 }}>
                            {lawData === undefined ? (
                              <span style={{ color: "#9ca3af" }}>
                                불러오는 중…
                              </span>
                            ) : lawData === null ? (
                              <span style={{ color: "#b91c1c" }}>
                                요건 정보를 불러오지 못했습니다
                              </span>
                            ) : lawData.length === 0 ? (
                              <span style={{ color: "#9ca3af" }}>
                                요건 정보 없음
                              </span>
                            ) : (
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  marginTop: 4,
                                }}
                              >
                                <thead>
                                  <tr
                                    style={{
                                      color: "#9ca3af",
                                      textAlign: "left",
                                    }}
                                  >
                                    <th
                                      style={{
                                        padding: "3px 6px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      법령
                                    </th>
                                    <th
                                      style={{
                                        padding: "3px 6px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      수입요건
                                    </th>
                                    <th
                                      style={{
                                        padding: "3px 6px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      사후관리
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {lawData.map((l, i) => (
                                    <tr
                                      key={i}
                                      style={{ borderTop: "1px solid #f0f0f0" }}
                                    >
                                      <td style={{ padding: "3px 6px" }}>
                                        {l.divi || "—"}
                                      </td>
                                      <td style={{ padding: "3px 6px" }}>
                                        {l.im_requisite || "—"}
                                      </td>
                                      <td style={{ padding: "3px 6px" }}>
                                        {l.after_care || "—"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </details>
                      )}
                      <button
                        disabled={
                          !resolveConfirmHsk(selected, editedHsk) ||
                          confirmed.has(selected.keyword)
                        }
                        onClick={() => doConfirm(selected)}
                        style={{ marginTop: 16 }}
                      >
                        {confirmed.has(selected.keyword)
                          ? "확정됨 ✓"
                          : "이 세번 확정"}
                      </button>
                    </div>
                  );
                })()
              ) : (
                <div
                  style={{
                    color: "#9ca3af",
                    fontSize: 13,
                    padding: "16px 4px",
                  }}
                >
                  행을 선택하면 근거가 표시됩니다. 방향키(↑↓)로 이동하세요.
                </div>
              )}
            </div>
          </div>
        </div>
        {step === STEP.EXPORT &&
          job?.status === "DONE" &&
          wb &&
          baseAoa &&
          effCols && (
            <div style={{ marginTop: 16, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                원본+세번 엑셀 다운로드
              </div>
              {/* 레이아웃 바 — 개인 저장 순서/표시 프리셋 선택·저장·삭제 */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: 8,
                }}
              >
                <span style={{ color: "#6b7280" }}>레이아웃</span>
                <select
                  value={selLayoutId}
                  onChange={(e) => onSelectLayout(e.target.value)}
                  style={{
                    padding: "5px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  <option value="">기본</option>
                  {(layouts || []).map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <Btn onClick={onSaveLayout} disabled={!selLayoutId}>
                  저장
                </Btn>
                <Btn onClick={onDeleteLayout} disabled={!selLayoutId}>
                  삭제
                </Btn>
                <input
                  value={newLayoutName}
                  onChange={(e) => setNewLayoutName(e.target.value)}
                  placeholder="새 레이아웃 이름"
                  maxLength={50}
                  style={{
                    width: 150,
                    padding: "5px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                />
                <Btn onClick={onSaveAsLayout} disabled={!newLayoutName.trim()}>
                  새 이름으로 저장
                </Btn>
                <Btn onClick={() => setColChipsOpen((v) => !v)}>
                  컬럼 표시 {colChipsOpen ? "▴" : "▾"}
                </Btn>
              </div>
              {colChipsOpen && (
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    marginBottom: 8,
                    padding: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                  }}
                >
                  {effCols.map((c) => (
                    <label
                      key={c.header}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        padding: "2px 8px",
                        border: `1px solid ${c.visible ? "#9ca3af" : "#e5e7eb"}`,
                        borderRadius: 999,
                        background: c.visible ? "#f3f4f6" : "#fff",
                        color: c.visible ? "#374151" : "#9ca3af",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={c.visible}
                        onChange={() => toggleCol(c.header)}
                        style={{ accentColor: "#6b7280" }}
                      />
                      {c.header}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ color: "#6b7280", marginBottom: 6, fontSize: 12 }}>
                컬럼을 끌어 순서를 바꾸고 표시 여부를 조정한 뒤 다운로드하면
                그대로 반영됩니다.
              </div>
              <div
                id={pvGridId}
                style={{ height: 380, marginTop: 4, marginBottom: 12 }}
              />
              <table style={{ borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "2px 12px 2px 0", color: "#6b7280" }}>
                      원본 행
                    </td>
                    <td>{preview?.totalRows ?? wb.rows.length}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "2px 12px 2px 0", color: "#6b7280" }}>
                      유니크 품명
                    </td>
                    <td>{preview?.uniqueCount ?? "-"}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "2px 12px 2px 0", color: "#6b7280" }}>
                      분류
                    </td>
                    <td>{job?.results?.length ?? 0}</td>
                  </tr>
                  {preview?.capped && (
                    <tr>
                      <td
                        style={{ padding: "2px 12px 2px 0", color: "#b45309" }}
                      >
                        미분류(캡 초과)
                      </td>
                      <td style={{ color: "#b45309" }}>
                        {preview.uniqueCount - preview.used}유니크
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div style={{ marginTop: 6, color: "#6b7280" }}>
                상품명 앞에 신고세번·원산지·세율구분·세율·FTA대상 컬럼이
                추가됩니다. 수동수정·이력반영 값이 우선 적용됩니다.
              </div>
              {ratesFailed && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#b45309" }}>
                  세율 정보를 불러오지 못해 세율구분·세율·FTA대상은 공란으로
                  출력됩니다.
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <Btn
                  primary
                  onClick={onDownload}
                  disabled={!job?.results?.length}
                >
                  엑셀 다운로드
                </Btn>
              </div>
            </div>
          )}
      </div>
      <style>{`
        .bulk-edited-cell { background: #fef9c3; }
        .bulk-review-row { background: #fff7ed; }
        .bulk-master-warn { color: #b45309; font-weight: 600; }
        .bulk-sim-strong { font-weight: 700; }
        .bulk-origin-diff { border-left: 2px solid #3b82f6; }
        .bulk-progress { margin: 8px 0; }
        .bulk-progress-head { font-size: 12px; color: #334155; margin-bottom: 4px; }
        .bulk-progress-track { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
        .bulk-progress-fill { height: 100%; background: #2563eb; transition: width .3s ease; }
      `}</style>
    </div>
  );
}
